import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";

// Import assets
import { Images } from "../../../../utils/Images";
import { getImageSrc } from "../../../../utils/imageMapping";

// Styled components
const CardContainer = styled(Box)(({ theme, isMobile, isTablet, isSmallScreen, isMediumScreen, isExtended, extendedHeight }) => {
  // Default heights
  let defaultHeight = isMobile ? "140px" : isSmallScreen ? "280px" : isMediumScreen ? "300px" : "356px";
  
  // If extended, use the extended height
  let cardHeight = defaultHeight;
  if (isExtended && !isMobile && extendedHeight) {
    cardHeight = `${extendedHeight}px`;
  }
  
  return {
  width: isMobile ? "328px" : isSmallScreen ? "330px" : isMediumScreen ? "380px" : "461px",
    height: cardHeight,
  borderRadius: isMobile ? "11.38px" : isSmallScreen ? "12px" : "16px",
  justifyContent: isMobile ? "center" : "space-between",
  padding: isMobile ? "11.38px" : "0px",
  paddingTop: isMobile ? "11.38px" : isSmallScreen ? "16px" : isMediumScreen ? "18px" : "20px",
  paddingRight: isMobile ? "11.38px" : isSmallScreen ? "12px" : isMediumScreen ? "13px" : "14.39px",
  paddingBottom: isMobile ? "11.38px" : isSmallScreen ? "16px" : isMediumScreen ? "18px" : "20px",
  paddingLeft: isMobile ? "11.38px" : isSmallScreen ? "12px" : isMediumScreen ? "13px" : "14.39px",
  gap: isMobile ? "8px" : "0px",
  opacity: 1,
  boxShadow: isMobile ? "0px 2.85px 13.65px 0px #0000001A" : "0px 2px 12px 0px #0000001A",
  background: "#FFFFFF",
  display: "flex",
  flexDirection: isMobile ? "row" : "column",
  alignItems: isMobile ? "center" : "stretch",
  };
});

const ImageContainer = styled(Box)(({ theme, isMobile, isTablet, isSmallScreen, isMediumScreen, backgroundColor, isExtended, extendedHeight }) => {
  // Default heights
  let defaultHeight = isMobile ? "118.81995391845703px" : isSmallScreen ? "120px" : isMediumScreen ? "140px" : "180px";
  
  // If extended, calculate the image height needed to fill extra space
  // We want to extend only the image section, keeping content section at natural height
  let imageHeight = defaultHeight;
  if (isExtended && !isMobile && extendedHeight) {
    // Default card height: smallScreen=280px, mediumScreen=300px, large=356px
    const defaultCardHeight = isSmallScreen ? 280 : isMediumScreen ? 300 : 356;
    // Default image height
    const defaultImageHeight = isSmallScreen ? 120 : isMediumScreen ? 140 : 180;
    // Calculate the difference in total card height
    const heightDifference = extendedHeight - defaultCardHeight;
    // Add that difference to the image height
    imageHeight = `${defaultImageHeight + heightDifference}px`;
  }
  
  return {
  width: isMobile ? "118.10845947265625px" : "auto",
    height: imageHeight,
  marginBottom: isMobile ? "0px" : isSmallScreen ? "16px" : isMediumScreen ? "18px" : "20px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  overflow: "hidden",
  borderRadius: isMobile ? "5.9px" : isSmallScreen ? "10px" : "12px",
  background: backgroundColor,
  "& img": {
    width: isMobile ? "80%" : "80%",
    height: isMobile ? "80%" : "80%",
    objectFit: isMobile ? "contain" : "contain",
    borderRadius: isMobile ? "5.9px" : isSmallScreen ? "10px" : "12px",
  },
  };
});

const ContentContainer = styled(Box)(({ theme, isMobile }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
}));

const TitlePriceSection = styled(Box)(({ theme, isMobile, isTablet, isSmallScreen, isMediumScreen }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: isMobile ? "8px" : isSmallScreen ? "12px" : isMediumScreen ? "13px" : "14px",
}));

const Title = styled(Typography)(({ theme, isMobile, isTablet, isSmallScreen, isMediumScreen }) => ({
  fontFamily: isMobile ? "Red Hat Display" : "inherit",
  fontWeight: 700,
  fontStyle: "Bold",
  fontSize: isMobile ? "14px" : isSmallScreen ? "14px" : isMediumScreen ? "16px" : "18px",
  lineHeight: "100%",
  letterSpacing: "0%",
  color: "#1D1D1B",
  marginBottom: isMobile ? "10px" : "0px",
}));

const PriceContainer = styled(Box)(({ theme, isMobile }) => ({
  display: "flex",
  alignItems: "center",
  gap: "2px",
}));

const PriceAmount = styled(Typography)(({ theme, isMobile, isTablet, isSmallScreen, isMediumScreen }) => ({
  fontFamily: isMobile ? "Red Hat Display" : "inherit",
  fontWeight: 900,
  fontStyle: "Black",
  fontSize: isMobile ? "12px" : isSmallScreen ? "14px" : isMediumScreen ? "16px" : "18px",
  lineHeight: "100%",
  letterSpacing: "-2%",
  color: "#9CBA4D",
}));

const PriceQuantity = styled(Typography)(({ theme, isMobile, isTablet, isSmallScreen, isMediumScreen }) => ({
  fontFamily: isMobile ? "Red Hat Display" : "inherit",
  fontWeight: 600,
  fontStyle: "SemiBold",
  fontSize: isMobile ? "12px" : isSmallScreen ? "14px" : isMediumScreen ? "16px" : "18px",
  lineHeight: "100%",
  letterSpacing: "-2%",
  color: "#4A4A49",
}));

const Divider = styled(Box)(({ theme, isMobile, isTablet, isSmallScreen, isMediumScreen }) => ({
  border: "0.78px solid #BEBFD0",
  marginBottom: isMobile ? "11px" : isSmallScreen ? "12px" : isMediumScreen ? "13px" : "14px",
  marginTop: isMobile ? "11px" : "0px",
}));

const BottomSection = styled(Box)(({ theme, isMobile, isTablet, isSmallScreen, isMediumScreen }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flex: 1,
}));

const ProgressSection = styled(Box)(({ theme, isMobile, isTablet, isSmallScreen, isMediumScreen }) => ({
  display: "flex",
  alignItems: "center",
  gap: isMobile ? "6px" : isSmallScreen ? "8px" : isMediumScreen ? "10px" : "12px",
}));

const ProgressCircle = styled(Box)(({ theme, isMobile, isTablet, isSmallScreen, isMediumScreen, progress }) => ({
  width: isMobile ? "36px" : isSmallScreen ? "44px" : isMediumScreen ? "52px" : "64px",
  height: isMobile ? "36px" : isSmallScreen ? "44px" : isMediumScreen ? "52px" : "64px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
}));

const ProgressText = styled(Typography)(({ theme, isMobile, isTablet, isSmallScreen, isMediumScreen }) => ({
  // fontFamily: "Red Hat Display",
  fontWeight: 700,
  fontStyle: "Bold",
  fontSize: isMobile ? "8px" : isSmallScreen ? "10px" : isMediumScreen ? "12px" : "14px",
  lineHeight: "100%",
  color: "#1D1D1B",
  position: "relative",
  zIndex: 1,
}));

const ProgressInfo = styled(Box)(({ theme, isMobile, isTablet, isSmallScreen, isMediumScreen }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "2px",
}));

const ObtainedGoalText = styled(Typography)(({ theme, isMobile, isTablet, isSmallScreen, isMediumScreen }) => ({
  fontFamily: isMobile ? "Red Hat Display" : "inherit",
  fontWeight: 700,
  fontStyle: "Bold",
  fontSize: isMobile ? "10px" : isSmallScreen ? "12px" : isMediumScreen ? "14px" : "16px",
  lineHeight: "100%",
  letterSpacing: "0%",
  color: "#1D1D1B",
}));

const QuantityObtainedText = styled(Typography)(({ theme, isMobile, isTablet, isSmallScreen, isMediumScreen }) => ({
  fontFamily: isMobile ? "Red Hat Display" : "inherit",
  fontWeight: 400,
  fontStyle: "Regular",
  fontSize: isMobile ? "10px" : isSmallScreen ? "12px" : isMediumScreen ? "14px" : "16px",
  lineHeight: "100%",
  letterSpacing: "0%",
  color: "#979796",
}));

const QuantitySelector = styled(Box)(({ theme, isMobile, isTablet, isSmallScreen, isMediumScreen }) => ({
  width: isMobile ? "77.55314636230469px" : isSmallScreen ? "100px" : isMediumScreen ? "110px" : "129px",
  height: isMobile ? "34.86334228515625px" : isSmallScreen ? "38px" : isMediumScreen ? "42px" : "50px",
  borderRadius: isMobile ? "6.82px" : isSmallScreen ? "8px" : isMediumScreen ? "9px" : "10px",
  paddingTop: isMobile ? "5.12px" : isSmallScreen ? "6px" : isMediumScreen ? "7px" : "8px",
  paddingRight: isMobile ? "6.82px" : isSmallScreen ? "8px" : isMediumScreen ? "8.5px" : "9.59px",
  paddingBottom: isMobile ? "5.12px" : isSmallScreen ? "6px" : isMediumScreen ? "7px" : "8px",
  paddingLeft: isMobile ? "6.82px" : isSmallScreen ? "8px" : isMediumScreen ? "8.5px" : "9.59px",
  gap: isMobile ? "11.38px" : isSmallScreen ? "12px" : isMediumScreen ? "14px" : "18px",
  opacity: 1,
  border: isMobile ? "0.85px solid #1D1D1B" : "1.2px solid #1D1D1B",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const IconButton = styled(Box)(({ theme, isMobile, isTablet, isSmallScreen, isMediumScreen }) => ({
  width: isMobile ? "12px" : isSmallScreen ? "14px" : isMediumScreen ? "16px" : "19.18px",
  height: isMobile ? "12px" : isSmallScreen ? "14px" : isMediumScreen ? "16px" : "19.18px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  "& img": {
    width: isMobile ? "12px" : isSmallScreen ? "14px" : isMediumScreen ? "16px" : "19.18px",
    height: isMobile ? "12px" : isSmallScreen ? "14px" : isMediumScreen ? "16px" : "19.18px",
  },
}));

const QuantityNumber = styled(Typography)(({ theme, isMobile, isTablet, isSmallScreen, isMediumScreen }) => ({
  // fontFamily: "Red Hat Display",
  fontWeight: 900,
  fontStyle: "Black",
  fontSize: isMobile ? "12px" : isSmallScreen ? "10px" : isMediumScreen ? "12px" : "14px",
  lineHeight: "100%",
  letterSpacing: "0%",
  textAlign: "center",
  color: "#1D1D1B",
}));

const ProductBigCard = ({ productData, cardIndex, isExtended = false, extendedHeight = null }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery("(max-width: 900px)");
  const isSmallScreen = useMediaQuery("(max-width: 1200px)");
  const isMediumScreen = useMediaQuery("(max-width: 1420px)");
  const { id, title, image, price, quantity: productQuantity, goal, obtained } = productData;
  
  // Initialize quantity from sessionStorage if product is already selected
  const [quantity, setQuantity] = useState(() => {
    try {
      const selectedProducts = JSON.parse(sessionStorage.getItem("selectedProducts") || "[]");
      const existingProduct = selectedProducts.find(p => p.id === id);
      return existingProduct ? existingProduct.quantity : 0;
    } catch (error) {
      return 0;
    }
  });

  // Sync quantity when sessionStorage is updated externally (e.g. reset on page entry or back-navigation)
  useEffect(() => {
    const syncQuantity = () => {
      try {
        const selectedProducts = JSON.parse(sessionStorage.getItem("selectedProducts") || "[]");
        const existing = selectedProducts.find(p => p.id === id);
        setQuantity(existing ? existing.quantity : 0);
      } catch {
        setQuantity(0);
      }
    };
    window.addEventListener('selectedProductsUpdated', syncQuantity);
    return () => window.removeEventListener('selectedProductsUpdated', syncQuantity);
  }, [id]);
  
  const displayedObtained = obtained + quantity;
  const progressPercentage = Math.round((displayedObtained / goal) * 100);

  // Big card background colors array
  const bigCardColors = [
    "#F9EEC675", // First color
    "#ECFAE5",   // Second color
    "#F0F8FF",   // Light blue
    "#FFF8DC",   // Light yellow
    "#F5F5DC",   // Beige
    "#E6E6FA",   // Lavender
    "#F0FFF0",   // Honeydew
    "#FFF0F5",   // Lavender blush
    "#F5FFFA",   // Mint cream
    "#FFFAF0",   // Floral white
  ];

  // Get background color based on card index (cycles through colors)
  const backgroundColor = bigCardColors[cardIndex % bigCardColors.length];

  // Helper function to update selected products in sessionStorage
  const updateSelectedProducts = (newQuantity) => {
    try {
      const selectedProducts = JSON.parse(sessionStorage.getItem("selectedProducts") || "[]");
      const productIndex = selectedProducts.findIndex(p => p.id === id);
      
      if (newQuantity > 0) {
        const productData = {
          id,
          title,
          image: getImageSrc(image),
          price,
          quantity: newQuantity,
          totalAmount: price * newQuantity
        };
        
        if (productIndex >= 0) {
          selectedProducts[productIndex] = productData;
        } else {
          selectedProducts.push(productData);
        }
      } else {
        // Remove product if quantity is 0
        if (productIndex >= 0) {
          selectedProducts.splice(productIndex, 1);
        }
      }
      
      sessionStorage.setItem("selectedProducts", JSON.stringify(selectedProducts));
      
      // Update count for backward compatibility
      const totalCount = selectedProducts.reduce((sum, p) => sum + p.quantity, 0);
      sessionStorage.setItem("selectedProductsCount", String(totalCount));
      
      // Trigger storage event for real-time updates
      window.dispatchEvent(new Event('storage'));
      window.dispatchEvent(new CustomEvent('selectedProductsUpdated'));
    } catch (error) {
      console.error("Error updating selected products:", error);
    }
  };

  const handleIncreaseQuantity = () => {
    const newQuantity = quantity + 1;
    setQuantity(newQuantity);
    updateSelectedProducts(newQuantity);
  };

  const handleDecreaseQuantity = () => {
    if (quantity > 0) {
      const newQuantity = quantity - 1;
      setQuantity(newQuantity);
      updateSelectedProducts(newQuantity);
    }
  };

  // Calculate responsive SVG dimensions
  const svgSize = isMobile ? 36 : isSmallScreen ? 44 : isMediumScreen ? 52 : 64;
  const circleRadius = isMobile ? 14 : isSmallScreen ? 18 : isMediumScreen ? 22 : 28;
  const strokeWidth = isMobile ? 2 : isSmallScreen ? 2.5 : isMediumScreen ? 3 : 4;

  return (
    <CardContainer isMobile={isMobile} isTablet={isTablet} isSmallScreen={isSmallScreen} isMediumScreen={isMediumScreen} isExtended={isExtended} extendedHeight={extendedHeight}>
      {/* Image Section */}
      <ImageContainer isMobile={isMobile} isTablet={isTablet} isSmallScreen={isSmallScreen} isMediumScreen={isMediumScreen} backgroundColor={backgroundColor} isExtended={isExtended} extendedHeight={extendedHeight}>
        <img src={getImageSrc(image)} alt={title} />
      </ImageContainer>

      {/* Content Section */}
      <ContentContainer isMobile={isMobile}>
        {isMobile ? (
          // Mobile layout - keep exactly as it was before
          <>
            {/* Title */}
            <Title isMobile={isMobile} isTablet={isTablet} isSmallScreen={isSmallScreen} isMediumScreen={isMediumScreen}>
              {title}
            </Title>

            {/* Progress Section */}
            <ProgressSection isMobile={isMobile} isTablet={isTablet} isSmallScreen={isSmallScreen} isMediumScreen={isMediumScreen}>
              <ProgressCircle isMobile={isMobile} isTablet={isTablet} isSmallScreen={isSmallScreen} isMediumScreen={isMediumScreen} progress={progressPercentage}>
                <svg
                  width={svgSize}
                  height={svgSize}
                  viewBox={`0 0 ${svgSize} ${svgSize}`}
                  style={{ position: "absolute", top: 0, left: 0 }}
                >
                  {/* Background circle */}
                  <circle
                    cx={svgSize/2}
                    cy={svgSize/2}
                    r={circleRadius}
                    fill="none"
                    stroke="#E8E8E8"
                    strokeWidth={strokeWidth}
                  />
                  {/* Progress circle */}
                  <circle
                    cx={svgSize/2}
                    cy={svgSize/2}
                    r={circleRadius}
                    fill="none"
                    stroke="#29CC6A"
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    strokeDasharray={`${2 * Math.PI * circleRadius}`}
                    strokeDashoffset={`${2 * Math.PI * circleRadius * (1 - progressPercentage / 100)}`}
                    transform={`rotate(-90 ${svgSize/2} ${svgSize/2})`}
                    style={{
                      transition: "stroke-dashoffset 0.3s ease",
                    }}
                  />
                </svg>
                <ProgressText isMobile={isMobile} isTablet={isTablet} isSmallScreen={isSmallScreen} isMediumScreen={isMediumScreen}>
                  {progressPercentage}%
                </ProgressText>
              </ProgressCircle>
              <ProgressInfo isMobile={isMobile} isTablet={isTablet} isSmallScreen={isSmallScreen} isMediumScreen={isMediumScreen}>
                <ObtainedGoalText isMobile={isMobile} isTablet={isTablet} isSmallScreen={isSmallScreen} isMediumScreen={isMediumScreen}>
                  {displayedObtained} of {goal}
                </ObtainedGoalText>
                <QuantityObtainedText isMobile={isMobile} isTablet={isTablet} isSmallScreen={isSmallScreen} isMediumScreen={isMediumScreen}>
                  Quantity Obtained
                </QuantityObtainedText>
              </ProgressInfo>
            </ProgressSection>

            {/* Divider */}
            <Divider isMobile={isMobile} isTablet={isTablet} isSmallScreen={isSmallScreen} isMediumScreen={isMediumScreen} />

            {/* Bottom Section */}
            <BottomSection isMobile={isMobile} isTablet={isTablet} isSmallScreen={isSmallScreen} isMediumScreen={isMediumScreen}>
              {/* Price Container */}
              <PriceContainer isMobile={isMobile}>
                <PriceAmount isMobile={isMobile} isTablet={isTablet} isSmallScreen={isSmallScreen} isMediumScreen={isMediumScreen}>
                  ₹{price}
                </PriceAmount>
                <PriceQuantity isMobile={isMobile} isTablet={isTablet} isSmallScreen={isSmallScreen} isMediumScreen={isMediumScreen}>
                  /{productQuantity}
                </PriceQuantity>
              </PriceContainer>

              {/* Quantity Selector */}
              <QuantitySelector isMobile={isMobile} isTablet={isTablet} isSmallScreen={isSmallScreen} isMediumScreen={isMediumScreen}>
                <IconButton 
                  isMobile={isMobile} 
                  isTablet={isTablet} 
                  isSmallScreen={isSmallScreen} 
                  isMediumScreen={isMediumScreen}
                  onClick={handleDecreaseQuantity}
                >
                  <img src={Images.pminus} alt="Decrease" />
                </IconButton>
                <QuantityNumber isMobile={isMobile} isTablet={isTablet} isSmallScreen={isSmallScreen} isMediumScreen={isMediumScreen}>
                  {quantity.toString()}
                </QuantityNumber>
                <IconButton 
                  isMobile={isMobile} 
                  isTablet={isTablet} 
                  isSmallScreen={isSmallScreen} 
                  isMediumScreen={isMediumScreen}
                  onClick={handleIncreaseQuantity}
                >
                  <img src={Images.pplus} alt="Increase" />
                </IconButton>
              </QuantitySelector>
            </BottomSection>
          </>
        ) : (
          // Desktop layout - new structure
          <>
            {/* Title and Price Section */}
            <TitlePriceSection isTablet={isTablet} isSmallScreen={isSmallScreen} isMediumScreen={isMediumScreen}>
              <Title isTablet={isTablet} isSmallScreen={isSmallScreen} isMediumScreen={isMediumScreen}>
                {title}
              </Title>
              <PriceContainer>
                <PriceAmount isTablet={isTablet} isSmallScreen={isSmallScreen} isMediumScreen={isMediumScreen}>
                  ₹{price}
                </PriceAmount>
                <PriceQuantity isTablet={isTablet} isSmallScreen={isSmallScreen} isMediumScreen={isMediumScreen}>
                  /{productQuantity}
                </PriceQuantity>
              </PriceContainer>
            </TitlePriceSection>

            {/* Divider */}
            <Divider isTablet={isTablet} isSmallScreen={isSmallScreen} isMediumScreen={isMediumScreen} />

            {/* Bottom Section */}
            <BottomSection isTablet={isTablet} isSmallScreen={isSmallScreen} isMediumScreen={isMediumScreen}>
              {/* Progress Section */}
              <ProgressSection isTablet={isTablet} isSmallScreen={isSmallScreen} isMediumScreen={isMediumScreen}>
                <ProgressCircle isTablet={isTablet} isSmallScreen={isSmallScreen} isMediumScreen={isMediumScreen} progress={progressPercentage}>
                  <svg
                    width={svgSize}
                    height={svgSize}
                    viewBox={`0 0 ${svgSize} ${svgSize}`}
                    style={{ position: "absolute", top: 0, left: 0 }}
                  >
                    {/* Background circle */}
                    <circle
                      cx={svgSize/2}
                      cy={svgSize/2}
                      r={circleRadius}
                      fill="none"
                      stroke="#E8E8E8"
                      strokeWidth={strokeWidth}
                    />
                    {/* Progress circle */}
                    <circle
                      cx={svgSize/2}
                      cy={svgSize/2}
                      r={circleRadius}
                      fill="none"
                      stroke="#29CC6A"
                      strokeWidth={strokeWidth}
                      strokeLinecap="round"
                      strokeDasharray={`${2 * Math.PI * circleRadius}`}
                      strokeDashoffset={`${2 * Math.PI * circleRadius * (1 - progressPercentage / 100)}`}
                      transform={`rotate(-90 ${svgSize/2} ${svgSize/2})`}
                      style={{
                        transition: "stroke-dashoffset 0.3s ease",
                      }}
                    />
                  </svg>
                  <ProgressText isTablet={isTablet} isSmallScreen={isSmallScreen} isMediumScreen={isMediumScreen}>
                    {progressPercentage}%
                  </ProgressText>
                </ProgressCircle>
                <ProgressInfo isTablet={isTablet} isSmallScreen={isSmallScreen} isMediumScreen={isMediumScreen}>
                  <ObtainedGoalText isTablet={isTablet} isSmallScreen={isSmallScreen} isMediumScreen={isMediumScreen}>
                  {displayedObtained} of {goal}
                  </ObtainedGoalText>
                  <QuantityObtainedText isTablet={isTablet} isSmallScreen={isSmallScreen} isMediumScreen={isMediumScreen}>
                    Quantity Obtained
                  </QuantityObtainedText>
                </ProgressInfo>
              </ProgressSection>

              {/* Quantity Selector */}
              <QuantitySelector isTablet={isTablet} isSmallScreen={isSmallScreen} isMediumScreen={isMediumScreen}>
                <IconButton 
                  isTablet={isTablet} 
                  isSmallScreen={isSmallScreen} 
                  isMediumScreen={isMediumScreen}
                  onClick={handleDecreaseQuantity}
                >
                  <img src={Images.pminus} alt="Decrease" />
                </IconButton>
                <QuantityNumber isTablet={isTablet} isSmallScreen={isSmallScreen} isMediumScreen={isMediumScreen}>
                  {quantity.toString()}
                </QuantityNumber>
                <IconButton 
                  isTablet={isTablet} 
                  isSmallScreen={isSmallScreen} 
                  isMediumScreen={isMediumScreen}
                  onClick={handleIncreaseQuantity}
                >
                  <img src={Images.pplus} alt="Increase" />
                </IconButton>
              </QuantitySelector>
            </BottomSection>
          </>
        )}
      </ContentContainer>
    </CardContainer>
  );
};

export default ProductBigCard;
